import useControlPlane from '@/hooks/use-control-plane';
import { useCurrentTenantId } from '@/hooks/use-tenant';
import {
  V1AdditionalMetadataOperator,
  V1RunningFilter,
  V1TaskStatus,
  Workflow,
} from '@/lib/api';
import api, { SELF_HOSTED_LIST_TIMEOUT_MS } from '@/lib/api/api';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

const COUNT_BATCH_SIZE = 5;
const COUNT_LIMIT = 20_000;

type WorkflowCount = {
  workflowId: string;
  count?: number;
};

export const useWorkflowRunCounts = (
  workflows: Workflow[],
  enabled: boolean,
  createdAfter: string,
  finishedBefore: string | undefined,
  statuses: V1TaskStatus[] | undefined,
  runningFilter: V1RunningFilter | undefined,
  additionalMetadata: string[] | undefined,
  additionalMetadataOperator: V1AdditionalMetadataOperator,
  idempotencyKeys: string[] | undefined,
  workerId: string | undefined,
  parentTaskExternalId: string | undefined,
  triggeringEventExternalId: string | undefined,
  onlyTasks: boolean,
) => {
  const { tenantId } = useCurrentTenantId();
  const { isSelfHosted } = useControlPlane();
  const workflowIds = workflows.map((workflow) => workflow.metadata.id);

  const countsQuery = useQuery({
    queryKey: [
      'workflow-filter:run-counts',
      tenantId,
      workflowIds,
      createdAfter,
      finishedBefore,
      statuses,
      runningFilter,
      additionalMetadata,
      additionalMetadataOperator,
      idempotencyKeys,
      workerId,
      parentTaskExternalId,
      triggeringEventExternalId,
      onlyTasks,
    ],
    enabled: enabled && workflowIds.length > 0,
    queryFn: async ({ signal }) => {
      const fetchCount = async (workflowId: string): Promise<WorkflowCount> => {
        try {
          const { data } = await api.v1WorkflowRunList(
            tenantId,
            {
              offset: 0,
              limit: 1,
              statuses: statuses?.length ? statuses : undefined,
              workflow_ids: [workflowId],
              parent_task_external_id: parentTaskExternalId,
              since: createdAfter,
              until: finishedBefore,
              additional_metadata: additionalMetadata,
              idempotency_keys: idempotencyKeys,
              additional_metadata_operator: additionalMetadataOperator,
              worker_id: workerId,
              only_tasks: onlyTasks,
              triggering_event_external_id: triggeringEventExternalId,
              include_payloads: false,
              running_filter: runningFilter,
            },
            {
              signal,
              timeout: isSelfHosted ? SELF_HOSTED_LIST_TIMEOUT_MS : undefined,
            },
          );
          return { workflowId, count: data.pagination.num_pages ?? 0 };
        } catch (error) {
          if (signal.aborted) {
            throw error;
          }
          return { workflowId };
        }
      };

      const counts: WorkflowCount[] = [];
      for (
        let index = 0;
        index < workflowIds.length;
        index += COUNT_BATCH_SIZE
      ) {
        counts.push(
          ...(await Promise.all(
            workflowIds.slice(index, index + COUNT_BATCH_SIZE).map(fetchCount),
          )),
        );
      }
      return counts;
    },
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  return useMemo(() => {
    const measured = countsQuery.data?.filter(
      (result): result is Required<WorkflowCount> => result.count !== undefined,
    );
    const counts = new Map(
      measured?.map(({ workflowId, count }) => [workflowId, count]),
    );
    const cappedWorkflowIds = new Set(
      measured
        ?.filter(({ count }) => count === COUNT_LIMIT)
        .map(({ workflowId }) => workflowId),
    );
    return {
      counts,
      cappedWorkflowIds,
      isLoading: countsQuery.isFetching,
    };
  }, [countsQuery.data, countsQuery.isFetching]);
};
